/**
 * Route prefetch utilities — hover/touch intent, LCP observer, preload audit.
 */

// ─── Chunk prefetch ───────────────────────────────────────────────────────────

const prefetchedChunks = new Set<string>();

/** Fire-and-forget dynamic import with deduplication. */
export function prefetchChunk(importFn: () => Promise<unknown>): Promise<void> {
  const key = importFn.toString();
  if (prefetchedChunks.has(key)) return Promise.resolve();
  prefetchedChunks.add(key);
  return importFn().then(() => undefined).catch(() => undefined);
}

// ─── Hover / touch prefetch ───────────────────────────────────────────────────

const hoverPrefetchedRoutes = new Set<string>();

const IMPORT_MAP: Record<string, () => Promise<unknown>> = {
  '/inventory':           () => import('../pages/inventory/page'),
  '/vehicle-detail':      () => import('../pages/vehicle-detail/page'),
  '/financing':           () => import('../pages/financing/page'),
  '/sell-your-car':       () => import('../pages/sell-your-car/page'),
  '/trade-in':            () => import('../pages/trade-in/page'),
  '/consignment':         () => import('../pages/consignment/page'),
  '/apply-for-financing': () => import('../pages/apply-for-financing/page'),
  '/broker-services':     () => import('../pages/broker-services/page'),
  '/contact':             () => import('../pages/contact/page'),
  '/service-areas':       () => import('../pages/service-areas/page'),
  '/blog':                () => import('../pages/blog/page'),
  '/videos':              () => import('../pages/videos/page'),
  '/faq':                 () => import('../pages/faq/page'),
  '/car-buying-guide':    () => import('../pages/car-buying-guide/page'),
  '/reviews':             () => import('../pages/reviews/page'),
  '/compare':             () => import('../pages/compare/page'),
  '/how-it-works':        () => import('../pages/how-it-works/page'),
  '/community-impact':    () => import('../pages/community-impact/page'),
  '/military-discount':   () => import('../pages/military-discount/page'),
  '/about-the-owner':     () => import('../pages/about-the-owner/page'),
  '/deal-radar':          () => import('../pages/deal-radar/page'),
  '/cars-under-10k':      () => import('../pages/cars-under-10k/page'),
  '/cars-under-30k':      () => import('../pages/cars-under-30k/page'),
  '/sold-vehicles':       () => import('../pages/sold-vehicles/page'),
};

/** Warm a lazy page chunk on hover. Deduped per session. */
export function prefetchRouteOnHover(route: string): void {
  if (hoverPrefetchedRoutes.has(route) || typeof window === 'undefined') return;
  hoverPrefetchedRoutes.add(route);
  IMPORT_MAP[route]?.().catch(() => undefined);
}

/** Warm multiple routes on touchstart (shares the same dedup set). */
export function prefetchRoutesOnTouch(routes: string[]): void {
  routes.forEach(prefetchRouteOnHover);
}

// ─── Preload hint helpers ─────────────────────────────────────────────────────

function getPreloadHint(imageUrl: string): HTMLLinkElement | null {
  if (!imageUrl || typeof document === 'undefined') return null;
  return Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel="preload"][as="image"]')
  ).find((link) => {
    const href = link.href ?? '';
    const srcset = link.getAttribute('imagesrcset') ?? '';
    return href === imageUrl || srcset.includes(imageUrl) || imageUrl.includes(href.split('?')[0]);
  }) ?? null;
}

function hasPreloadHint(imageUrl: string): boolean {
  return getPreloadHint(imageUrl) !== null;
}

/** Returns true/false if hint exists, null if no hint at all. */
function preloadHintHasFetchPriority(imageUrl: string): boolean | null {
  const hint = getPreloadHint(imageUrl);
  if (!hint) return null;
  const attr = hint.getAttribute('fetchpriority') ?? (hint as any).fetchPriority ?? '';
  return attr.toLowerCase() === 'high';
}

// ─── Clipboard helper ─────────────────────────────────────────────────────────

function copyFixToClipboard(tag: string, context: string): void {
  if (typeof navigator === 'undefined') return;
  if (!import.meta.env.DEV) return;
  const log = () => console.log(`%c📋 [COPY FIX] Copied (${context}):\n${tag}`, 'color:#10b981;font-weight:bold;');
  const warn = () => console.warn(`[COPY FIX] Blocked (${context}). Paste manually:\n\n${tag}`);

  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(tag).then(log, warn);
    return;
  }
  try {
    const ta = Object.assign(document.createElement('textarea'), {
      value: tag,
      style: 'position:fixed;opacity:0',
    });
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    log();
  } catch {
    warn();
  }
}

// ─── Buy menu preload audit ───────────────────────────────────────────────────

let inventoryPreloadAuditRun = false;

/** Validate /inventory hero preload hint on Buy button hover. Runs once per session. */
export function auditInventoryPreloadHint(): void {
  if (!import.meta.env.DEV) return;
  if (inventoryPreloadAuditRun || typeof document === 'undefined') return;
  inventoryPreloadAuditRun = true;

  const allPreloads = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel="preload"][as="image"]')
  );
  const heroPreloads = allPreloads.filter(
    (l) => !l.href.includes('remixicon') && !l.href.includes('logo')
  );

  console.group('%c🔍 [BUY MENU HOVER] Inventory Hero Preload Audit', 'color:#8b5cf6;font-weight:bold;font-size:13px;');
  console.log('%cValidating /inventory hero preload hint…', 'color:#6b7280;font-size:12px;');

  if (allPreloads.length === 0) {
    const tag = `<link rel="preload" as="image" href="YOUR_INVENTORY_HERO_URL" fetchpriority="high" />`;
    console.warn(`[PRELOAD AUDIT] ⚠️ No image preload hints found. Add to index.html:\n\n  ${tag}`);
    copyFixToClipboard(tag, 'Buy menu — no hints');
    console.groupEnd();
    return;
  }

  console.log(`Found ${allPreloads.length} image preload hint(s):`);
  allPreloads.forEach((link, i) => {
    const href = link.href ?? '(no href)';
    const hasFP = (link.getAttribute('fetchpriority') ?? (link as any).fetchPriority ?? '').toLowerCase() === 'high';
    const isHero = heroPreloads.includes(link);
    const label = isHero ? ' ← likely inventory hero' : '';

    if (hasFP) {
      console.log(`%c  [${i + 1}] ✅ fetchpriority="high" present${label}`, 'color:#10b981;font-weight:600;', `\n      href: ${href.slice(0, 100)}`);
    } else {
      const tag = `<link rel="preload" as="image" href="${href}" fetchpriority="high" />`;
      console.warn(`  [${i + 1}] ⚠️ fetchpriority="high" MISSING${label}\n      href: ${href.slice(0, 100)}`);
      copyFixToClipboard(tag, `Buy menu — hint [${i + 1}]${label}`);
    }
  });

  if (heroPreloads.length === 0) {
    console.warn('[PRELOAD AUDIT] ⚠️ No dedicated inventory hero preload hint identified.');
  } else {
    const hero = heroPreloads[0];
    const hasFP = (hero.getAttribute('fetchpriority') ?? (hero as any).fetchPriority ?? '').toLowerCase() === 'high';
    if (hasFP) {
      console.log('%c\n✅ Inventory hero preload hint correctly configured.', 'color:#10b981;font-weight:bold;');
    } else {
      const tag = `<link rel="preload" as="image" href="${hero.href}" fetchpriority="high" />`;
      console.warn(`\n⚠️ Hero hint exists but missing fetchpriority="high".\nFix: ${tag}`);
      copyFixToClipboard(tag, 'Buy menu — hero missing fetchpriority');
    }
  }
  console.groupEnd();
}

// ─── Cumulative LCP session log ───────────────────────────────────────────────

interface LCPSessionEntry {
  page: string;
  lcp_ms: number;
  rating: string;
  element: string;
  has_preload: string;
  has_fetchpriority: string;
}

const lcpSessionLog: LCPSessionEntry[] = [];
let sessionSummaryInstalled = false;

function lcpRating(ms: number): string {
  if (ms <= 1800) return '🟢 GOOD';
  if (ms <= 2500) return '🟡 NEEDS IMPROVEMENT';
  return '🔴 SLOW';
}

function installSessionSummaryListener(): void {
  if (sessionSummaryInstalled || typeof window === 'undefined') return;
  sessionSummaryInstalled = true;

  const flush = () => {
    if (!import.meta.env.DEV) return;
    if (lcpSessionLog.length === 0) return;
    const times = lcpSessionLog.map((e) => e.lcp_ms);
    const avg = Math.round(times.reduce((a, b) => a + b, 0) / times.length);
    const counts = { good: 0, needs: 0, slow: 0 };
    lcpSessionLog.forEach((e) => {
      if (e.rating === '🟢 GOOD') counts.good++;
      else if (e.rating === '🟡 NEEDS IMPROVEMENT') counts.needs++;
      else counts.slow++;
    });

    console.group('%c📊 LCP Session Summary', 'color:#6366f1;font-weight:bold;font-size:14px;');
    console.log(`%c${lcpSessionLog.length} page(s) measured`, 'color:#6b7280;font-size:12px;');
    console.table(lcpSessionLog);
    console.log(
      `%cAvg: ${avg}ms  |  Best: ${Math.min(...times)}ms  |  Worst: ${Math.max(...times)}ms  |  🟢 ${counts.good}  🟡 ${counts.needs}  🔴 ${counts.slow}`,
      'color:#374151;font-weight:600;font-size:12px;'
    );
    console.groupEnd();
  };

  window.addEventListener('pagehide', flush);
  window.addEventListener('beforeunload', flush);
}

function recordLCPSession(route: string, time: number, elDesc: string, imageUrl: string): void {
  const hasPL = imageUrl ? hasPreloadHint(imageUrl) : null;
  const hasFP = hasPL ? preloadHintHasFetchPriority(imageUrl) : null;

  lcpSessionLog.push({
    page: route,
    lcp_ms: time,
    rating: lcpRating(time),
    element: elDesc.length > 60 ? `${elDesc.slice(0, 57)}...` : elDesc,
    has_preload: imageUrl ? (hasPL ? '✅ yes' : '⚠️ missing') : 'n/a (text)',
    has_fetchpriority: imageUrl
      ? hasFP === true ? '✅ yes' : hasFP === false ? '⚠️ missing' : '⚠️ no hint'
      : 'n/a (text)',
  });
  installSessionSummaryListener();
}

// ─── LCP Observer ────────────────────────────────────────────────────────────

const SLOW_LCP_MS = 2500;
/** Cap to avoid false positives from idle tabs / preview environments */
const MAX_REALISTIC_LCP_MS = 10_000;

let lcpObserverInstalled = false;
let activeLCPObserver: PerformanceObserver | null = null;
let lastLCPEntry: (PerformanceEntry & { element?: Element; url?: string; startTime: number }) | null = null;

function describeElement(el: Element | undefined): string {
  if (!el) return 'unknown';
  const tag = el.tagName.toLowerCase();
  const src = (el as HTMLImageElement).src ?? (el as HTMLImageElement).currentSrc ?? '';
  const cls = el.className ? `.${String(el.className).trim().split(/\s+/).slice(0, 2).join('.')}` : '';
  return src ? `<${tag} src="${src.slice(0, 80)}...">` : `<${tag}${cls}>`;
}

function reportLCP(route: string): void {
  if (!import.meta.env.DEV) {
    lastLCPEntry = null;
    return;
  }
  if (!lastLCPEntry) return;

  const rawTime = Math.round(lastLCPEntry.startTime);

  // Discard measurements that are clearly from idle tabs or preview environments
  if (rawTime > MAX_REALISTIC_LCP_MS) {
    lastLCPEntry = null;
    return;
  }

  const time = rawTime;
  const el = lastLCPEntry.element;
  const url = lastLCPEntry.url ?? '';
  const elDesc = describeElement(el);
  const isSlow = time > SLOW_LCP_MS;

  console.group(
    isSlow ? `%c[LCP] 🔴 SLOW — ${time}ms — ${route}` : `%c[LCP] ${time}ms — ${route}`,
    isSlow ? 'color:#ef4444;font-weight:bold;font-size:13px;' : 'color:#10b981;font-weight:bold;'
  );

  if (isSlow) {
    console.error(
      `[LCP SLOW] ⚠️ ${time}ms on ${route} exceeds ${SLOW_LCP_MS}ms threshold.\n` +
      `Investigate: missing preload hint, render-blocking resources, slow server response, or large unoptimised image.`
    );
  }

  console.log('Element :', elDesc);

  if (url) {
    console.log('Resource:', url);
    const hasPL = hasPreloadHint(url);
    const hasFP = hasPL ? preloadHintHasFetchPriority(url) : null;

    if (hasPL && hasFP === true) {
      console.log('%c✅ Preload hint with fetchpriority="high" — correctly configured.', 'color:#10b981;font-weight:bold;');
    } else if (hasPL && hasFP === false) {
      const tag = `<link rel="preload" as="image" href="${url}" fetchpriority="high" />`;
      console.warn(`[LCP PRELOAD AUDIT] ⚠️ Preload hint on ${route} is missing fetchpriority="high".\nFix:\n  ${tag}`);
      copyFixToClipboard(tag, `${route} — missing fetchpriority`);
    } else {
      const tag = `<link rel="preload" as="image" href="${url}" fetchpriority="high" />`;
      console.warn(`[LCP PRELOAD AUDIT] ⚠️ No preload hint for LCP image on ${route}.\nAdd to <head>:\n  ${tag}`);
      copyFixToClipboard(tag, `${route} — no preload hint`);
    }
    console.log('%cConfirm this URL has a <link rel="preload" fetchpriority="high"> in index.html', 'color:#f59e0b;');
  } else {
    console.log('Resource: (text node — no image preload needed)');
  }

  console.log('Raw entry:', lastLCPEntry);
  console.groupEnd();

  recordLCPSession(route, time, elDesc, url);
  lastLCPEntry = null;
}

/** Re-arm the LCP observer for the current page. Call on every navigation. */
export function resetLCPObserver(): void {
  if (typeof window === 'undefined') return;
  if (!('PerformanceObserver' in window)) return;
  if (!(PerformanceObserver.supportedEntryTypes ?? []).includes('largest-contentful-paint')) return;

  activeLCPObserver?.disconnect();
  activeLCPObserver = null;
  lastLCPEntry = null;

  const route = window.location.pathname;
  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries() as Array<PerformanceEntry & { element?: Element; url?: string; startTime: number }>;
    if (entries.length > 0) lastLCPEntry = entries[entries.length - 1];
  });
  observer.observe({ type: 'largest-contentful-paint', buffered: true });
  activeLCPObserver = observer;

  const onInteract = () => {
    reportLCP(route);
    activeLCPObserver?.disconnect();
    activeLCPObserver = null;
  };

  ['click', 'keydown', 'scroll', 'touchstart'].forEach((evt) =>
    window.addEventListener(evt, onInteract, { once: true, passive: true, capture: true })
  );

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      reportLCP(route);
      activeLCPObserver?.disconnect();
      activeLCPObserver = null;
    }
  }, { once: true });
}

/** Install the LCP observer for the initial page load. */
export function installLCPObserver(): void {
  if (lcpObserverInstalled || typeof window === 'undefined') return;
  lcpObserverInstalled = true;
  resetLCPObserver();
}

// ─── Inventory image preload hints ───────────────────────────────────────────

const PRELOAD_ATTR = 'data-inv-preload';
const MAX_PRELOAD_IMAGES = 4; // Only preload above-the-fold cards (first row)

/**
 * Injects <link rel="preload" as="image"> hints for the first N vehicle images.
 * Deduped by href — safe to call multiple times.
 * Returns a cleanup function that removes the injected hints.
 */
export function injectInventoryImagePreloads(imageUrls: string[]): () => void {
  if (typeof document === 'undefined') return () => undefined;

  const injected: HTMLLinkElement[] = [];
  const existing = new Set(
    Array.from(document.querySelectorAll<HTMLLinkElement>(`link[${PRELOAD_ATTR}]`))
      .map((l) => l.href)
  );

  imageUrls.slice(0, MAX_PRELOAD_IMAGES).forEach((url) => {
    if (!url || url.startsWith('data:') || existing.has(url)) return;
    existing.add(url);

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = url;
    link.setAttribute('fetchpriority', 'high');
    link.setAttribute(PRELOAD_ATTR, '1');
    // imagesizes tells the browser which breakpoint to fetch
    link.setAttribute('imagesizes', '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw');
    document.head.appendChild(link);
    injected.push(link);
  });

  return () => {
    injected.forEach((l) => l.parentNode?.removeChild(l));
  };
}

/**
 * Reads the first N vehicle image URLs from the singleton vehicle cache
 * (already in memory from useVehicles) and injects preload hints.
 *
 * Call this on hover/touchstart over any "Inventory" nav link so the hints
 * are in the <head> before the route transition completes.
 */
export function preloadInventoryImages(): () => void {
  try {
    // Pull from sessionStorage warm cache (same key as useVehicles)
    const raw = sessionStorage.getItem('rha_vehicles_v1');
    if (!raw) return () => undefined;

    const parsed = JSON.parse(raw) as { vehicles: Array<{ images?: string[]; image?: string }> };
    const urls = (parsed.vehicles ?? [])
      .slice(0, MAX_PRELOAD_IMAGES)
      .map((v) => {
        const src = (Array.isArray(v.images) && v.images[0]) || (v as any).image || '';
        if (!src) return '';
        // Replicate thumbnailUrl logic inline to avoid require()
        const separator = src.includes('?') ? '&' : '?';
        return `${src}${separator}width=400&format=webp`;
      })
      .filter(Boolean);

    return injectInventoryImagePreloads(urls);
  } catch {
    return () => undefined;
  }
}
